import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './PortalHeader.module.css';
import { useAuth } from '../../context/AuthContext';

// Import logo as a module for better build handling
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';

interface PortalHeaderProps {
  title?: string;
  description?: string;
  className?: string;
  showCardIcon?: boolean;
  showContentHeader?: boolean;
}

const PortalHeader: React.FC<PortalHeaderProps> = ({ 
  title, 
  description, 
  className = "",
  showCardIcon = false,
  showContentHeader = true
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { showLoginForm, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isPortalLanding = location.pathname === '/portal/landing';

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles.headerContent}>
          {/* Left section: Mobile menu button + Logo */}
          <div className={styles.leftSection}>
            <button
              className={styles.menuButton}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarTopOpen : ''}`} />
              <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarMiddleOpen : ''}`} />
              <span className={`${styles.menuBar} ${isMobileMenuOpen ? styles.menuBarBottomOpen : ''}`} />
            </button>

            <Link to={"/portal/landing"} className={styles.logo}>
              <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
            </Link>
          </div>

          {/* Navigation Links (desktop) */}
          <nav className={styles.navigation}>
            <Link to={"/portal/landing"} className={styles.navLink}>Home</Link>
            <Link to="/partner-program" className={styles.navLink}>Partner Program</Link>
          </nav>

          {/* Right side buttons */}
          <div className={styles.rightSection}>
            {user ? (
              <div className={styles.userSection}>
                <div className={styles.userAvatar}>
                  <div className={styles.avatarPlaceholder}>
                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </div>
                </div>
                
                {showCardIcon && (
                  <button 
                    className={styles.settingsButton} 
                    onClick={() => navigate('/settings')}
                    title="Digital ID Card"
                    aria-label="Open Digital ID Card"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                      <path d="M6 8h2v2H6zm4 0h2v2h-2zm4 0h2v2h-2z"/>
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <button className={styles.loginButton} onClick={() => navigate('/portal')}>
                <svg className={styles.userIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Mobile navigation drawer */}
        <div
          className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.open : ''}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <nav className={`${styles.mobileNav} ${isMobileMenuOpen ? styles.open : ''}`}>
          <div className={styles.mobileNavHeader}>
            <span className={styles.mobileTitle}>Menu</span>
            <button
              className={styles.mobileClose}
              aria-label="Close menu"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              ×
            </button>
          </div>
        <div className={styles.mobileNavLinks} onClick={() => setIsMobileMenuOpen(false)}>
          <Link to={isPortalLanding ? "/portal/landing" : "/"} className={styles.mobileNavLink}>Home</Link>
          <Link to="/partner-program" className={styles.mobileNavLink}>Partner Program</Link>
        </div>
          <div className={styles.mobileActions}>
            {user ? (
              <div className={styles.mobileUserActions}>
                {showCardIcon && (
                  <button 
                    className={styles.mobileSettingsButton} 
                    onClick={() => { setIsMobileMenuOpen(false); navigate('/settings'); }}
                  >
                    Settings
                  </button>
                )}
                <button 
                  className={styles.mobileLogoutButton} 
                  onClick={() => { setIsMobileMenuOpen(false); }}
                >
                  Sign out
                </button>
              </div>
            ) : (
              <button 
                className={styles.mobileLoginButton} 
                onClick={() => { navigate('/portal'); setIsMobileMenuOpen(false); }}
              >
                Sign In
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Portal content header */}
      {showContentHeader && title && description && (
        <div className={`${styles.portalContentHeader} ${className}`}>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      )}
    </>
  );
};

export default PortalHeader;
